// Standing à-la-carte menu — transcribed from Khan Baba's printed menu (prices in KWD).
// This doubles as demo/seed data: the storefront shows it directly when no Firebase
// backend is configured, and the seed script writes it to the `menuItems` collection.
import type { Category, MenuItem } from "../lib/types";
import { MODIFIERS_BY_CATEGORY } from "./modifiers";

type Row = [name: string, nameAr: string, price: number];

const CATEGORIES: {
  key: Category;
  en: string;
  ar: string;
  icon: string;
  rows: Row[];
}[] = [
  {
    key: "chinese",
    en: "Chinese Foods",
    ar: "مأكولات صينية",
    icon: "🥡",
    rows: [
      ["Chicken Fried Rice", "رز بالدجاج مقلي", 1.75],
      ["Meat Fried Rice", "رز باللحم مقلي", 2.0],
      ["Prawns Fried Rice", "رز بالروبيان مقلي", 2.75],
      ["Mixed Fried Rice", "رز مخلط مقلي", 2.5],
      ["Vegetable Fried Rice", "رز بالخضار مقلي", 1.75],
      ["Egg Fried Rice", "رز بالبيض مقلي", 1.75],
      ["Vegetable Noodles", "معكرونة خضار", 1.75],
      ["Chicken Noodles", "معكرونة دجاج", 1.75],
      ["Mix Noodles", "معكرونة مشكل", 2.5],
      ["Prawns Noodles", "معكرونة روبيان", 2.75],
      ["Meat Noodles", "معكرونة بلحم", 2.0],
      ["Chicken Chilly", "دجاج فلفل حار", 2.0],
      ["Chicken Manchurian", "مانشوريان دجاج", 2.0],
      ["Prawns Manchurian", "مانشوريان روبيان", 2.75],
      ["Sweet Sour Chicken", "دجاج حامض حلو", 2.0],
      ["Sweet Sour Prawns", "روبيان حامض حلو", 2.75],
      ["Ginger Prawns", "روبيان بالزنجبيل", 2.75],
      ["Garlic Chicken", "دجاج بالثوم", 2.0],
      ["Ginger Chicken", "دجاج بالزنجبيل", 2.0],
      ["Beef Manchurian", "مانشوريان لحم البقر", 2.0],
      ["Gobi Manchurian", "مانشوريان زهرة", 1.75],
      ["Vegetable Manchurian", "مانشوريان خضار", 1.75],
      ["Prawns Chilly", "روبيان فلفل حار", 2.75],
      ["Beef Chilly", "لحم البقر حار", 2.0],
      ["Prawns Garlic", "روبيان صلصة ثوم", 2.75],
    ],
  },
  {
    key: "seafood",
    en: "Sea Foods",
    ar: "المأكولات البحرية",
    icon: "🦐",
    rows: [
      ["Fish Curry", "سمك كري", 2.75],
      ["Fish Masala", "سمك مسالا", 2.75],
      ["Prawns Masala", "روبيان مسالا", 2.9],
      ["Prawns Tikka", "تكة روبيان", 2.9],
      ["Prawns Fry", "روبيان مقلي", 2.9],
      ["Fish Fry", "سمك مقلي", 2.9],
      ["Prawns Curry", "روبيان كاري", 2.9],
      ["Prawns Kadai (Full)", "كراي روبيان (كامل)", 7.5],
      ["Prawns Kadai (Half)", "كراي روبيان (نصف)", 4.0],
      ["Fish Fry (Pomfret)", "سمك مقلي (زبيدي)", 3.5],
    ],
  },
  {
    key: "veg",
    en: "Vegetables",
    ar: "خضار",
    icon: "🥗",
    rows: [
      ["Mix Vegetable", "خضار مشكل", 1.75],
      ["Vegetable Korma", "قورمة خضار", 1.9],
      ["Chana Masala", "حمص مسالا", 1.25],
      ["Dal Fry", "عدس مقلي", 1.0],
      ["Dal Makhani", "عدس ماكاني", 1.5],
      ["Bhindi Masala", "بامية مسالا", 1.75],
      ["Aloo Palak", "بطاطا سبانخ", 1.75],
      ["Palak With Mushroom", "سبانخ بالمشروم", 1.75],
      ["Paneer Makhani", "بانير ماكاني", 1.75],
      ["Paneer Shahi Korma", "بانير شاهي قورمة", 1.75],
      ["Palak Paneer", "سبانخ بانير", 1.75],
    ],
  },
  {
    key: "breads",
    en: "Roti / Nan",
    ar: "نان / خبز",
    icon: "🫓",
    rows: [
      ["Tandoori Roti", "خبز تندوري", 0.1],
      ["Butter Roti", "خبز بالزبدة", 0.15],
      ["Sada Nan", "نان ساده", 0.15],
      ["Butter Nan", "نان بالزبدة", 0.2],
      ["Garlic Nan", "نان بالثوم", 0.4],
    ],
  },
  {
    key: "beverages",
    en: "Beverages",
    ar: "مشروبات",
    icon: "🥤",
    rows: [
      ["Tea", "شاي", 0.2],
      ["Mint Tea", "شاي بالنعناع", 0.3],
      ["Coffee", "قهوة", 0.45],
      ["Mineral Water", "ماء معدني", 0.25],
      ["Mango Juice", "عصير مانجو", 0.75],
      ["Orange Juice", "عصير برتقال", 0.75],
      ["Lassi", "لبن", 0.4],
      ["Lassi Mint", "لبن بالنعناع", 0.5],
      ["Pepsi / Cola / 7up", "بيبسي، كولا، سفن أب", 0.2],
      ["Cocktail Juice", "عصير كوكتيل", 0.75],
      ["Lemon Mint", "ليمون نعناع", 0.5],
      ["Lemon Juice", "عصير ليمون", 0.6],
      ["Strawberry Juice", "عصير فراولة", 0.9],
      ["Strawberry Shake", "فراولة شيك", 0.9],
      ["Banana Shake", "موز شيك", 0.9],
      ["Cocktail Shake", "كوكتيل شيك", 0.9],
      ["Mango Shake", "مانجو شيك", 0.9],
    ],
  },
  {
    key: "sweets",
    en: "Sweets",
    ar: "حلويات",
    icon: "🍮",
    rows: [
      ["Gulab Jamun (1 Plate)", "جلاب جامون (ماعون)", 0.5],
      ["Rasmalai (1 Plate)", "رسملاي (ماعون)", 0.6],
      ["Rasgulla (1 Plate)", "رسجلا (ماعون)", 0.5],
      ["Kheer", "كهير", 0.6],
      ["Ice Cream (Vanilla, Strawberry)", "آيس كريم (فانيلا، فراولة)", 0.75],
      ["Falooda", "فالودة", 0.75],
      ["Carrot Halwa (Winter, Plate)", "جزر حلوى (ماعون)", 1.25],
      ["Carrot Halwa (Winter, 1 KG)", "جزر حلوى (كيلو)", 6.0],
      ["Mix Sweets (1 KG)", "مشكل حلويات (كيلو)", 2.5],
      ["Special Motichoor Laddu (1 KG)", "موتي شور لدو خاص (كيلو)", 3.5],
    ],
  },
];

/** Category display metadata (id -> localized label + icon), in menu order. */
export const CATEGORY_META = CATEGORIES.map((c) => ({
  key: c.key,
  en: c.en,
  ar: c.ar,
  icon: c.icon,
}));

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[()/,.]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// A few signature dishes get a "Popular" flag for appetite cues.
const POPULAR = new Set([
  "Chicken Manchurian",
  "Prawns Masala",
  "Prawns Kadai (Full)",
  "Paneer Makhani",
  "Chicken Fried Rice",
  "Special Motichoor Laddu (1 KG)",
]);

export const MENU_SEED: MenuItem[] = CATEGORIES.flatMap((cat) =>
  cat.rows.map((row, i) => {
    const [name, nameAr, price] = row;
    return {
      id: `${cat.key}-${slug(name)}`,
      name,
      nameAr,
      category: cat.key,
      price,
      isMadeToOrder: true as const,
      active: true,
      sortOrder: i,
      tag: POPULAR.has(name) ? ("popular" as const) : undefined,
      options: MODIFIERS_BY_CATEGORY[cat.key],
    };
  }),
);
